import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import React, { useCallback, useEffect, useState } from 'react';
import {
  OrderReturnStatus,
  OrderStatus,
  OrderStatusTransitionPaymentRequirement,
} from '../../../clients/quipli-service/order/order';
import { toast } from '../../../util/toasts';
import usePrevious from '../../../hooks/use-previous';
import { debounce } from 'lodash';

export const OrderStatusTransitionPaymentRequirementConfig = () => {
  const [approvedRequirement, setApprovedRequirement] = useState<OrderStatusTransitionPaymentRequirement>();
  const prevApprovedRequirement = usePrevious(approvedRequirement);
  const [onRentRequirement, setOnRentRequirement] = useState<OrderStatusTransitionPaymentRequirement>();
  const prevOnRentRequirement = usePrevious(onRentRequirement);

  const { getStatusTransitionPaymentRequirement, upsertOrderStatusTransitionPaymentRequirement } = useOrderApiV1();

  useEffect(() => {
    getStatusTransitionPaymentRequirement(OrderStatus.Approved)
      .then((r) => setApprovedRequirement(r))
      .catch((e) => {
        console.error(e);
        toast('Failed to load status transition payment requirement', 'error');
      });
    getStatusTransitionPaymentRequirement(OrderReturnStatus.OnRent)
      .then((r) => setOnRentRequirement(r))
      .catch((e) => {
        console.error(e);
        toast('Failed to load status transition payment requirement', 'error');
      });
  }, []);

  function isValid(req: OrderStatusTransitionPaymentRequirement) {
    return (req.defaultPaymentPercentage || 0) >= (req.requiredPaymentPercentage || 0);
  }

  useEffect(() => {
    if (prevApprovedRequirement && approvedRequirement && isValid(approvedRequirement)) {
      debouncedDoUpdate(approvedRequirement);
    }
  }, [approvedRequirement]);

  useEffect(() => {
    if (
      prevOnRentRequirement &&
      onRentRequirement &&
      isValid(onRentRequirement) &&
      (approvedRequirement?.requiredPaymentPercentage || 0) <= (onRentRequirement.requiredPaymentPercentage || 0)
    ) {
      debouncedDoUpdate(onRentRequirement);
    }
  }, [onRentRequirement]);

  const debouncedDoUpdate = useCallback(debounce(doUpdate, 500), []);

  function doUpdate(req: OrderStatusTransitionPaymentRequirement) {
    upsertOrderStatusTransitionPaymentRequirement(req).catch((e) => {
      console.error(e);
      toast('Failed to update status transition payment requirement', 'error');
    });
  }

  return (
    <div>
      {approvedRequirement && (
        <div className="flex flex-column gap-4">
          <div className="font-bold">On Approve</div>
          <div className="flex items-center gap-4 pl-8">
            <div className="flex gap-2 items-center">
              <label>Default %:</label>
              <input
                type="number"
                value={approvedRequirement.defaultPaymentPercentage || ''}
                min={approvedRequirement.requiredPaymentPercentage || 0}
                max={100}
                onChange={(event) =>
                  setApprovedRequirement((existing) => {
                    return existing && { ...existing, defaultPaymentPercentage: parseFloat(event.target.value) };
                  })
                }
              />
            </div>
            <div className="flex gap-2 items-center">
              <label>Required %:</label>
              <input
                type="number"
                value={approvedRequirement.requiredPaymentPercentage || ''}
                min={0}
                max={100}
                onChange={(event) =>
                  setApprovedRequirement((existing) => {
                    return existing && { ...existing, requiredPaymentPercentage: parseFloat(event.target.value) };
                  })
                }
              />
            </div>
          </div>
          {(approvedRequirement.defaultPaymentPercentage || 0) <
            (approvedRequirement.requiredPaymentPercentage || 0) && (
            <span className="text-[var(--error-500)]">Default percentage must be greater or equal to required</span>
          )}
        </div>
      )}
      {approvedRequirement && onRentRequirement && (
        <div className="flex flex-column gap-4">
          <div className="font-bold">On Rent</div>
          <div className="flex items-center gap-4 pl-8">
            <div className="flex gap-2 items-center">
              <label>Default %:</label>
              <input
                type="number"
                value={onRentRequirement.defaultPaymentPercentage || ''}
                min={onRentRequirement.requiredPaymentPercentage || 0}
                max={100}
                onChange={(event) =>
                  setOnRentRequirement((existing) => {
                    return existing && { ...existing, defaultPaymentPercentage: parseFloat(event.target.value) };
                  })
                }
              />
            </div>
            <div className="flex gap-2 items-center">
              <label>Required %:</label>
              <input
                type="number"
                value={onRentRequirement.requiredPaymentPercentage || ''}
                min={approvedRequirement.requiredPaymentPercentage || 0}
                max={100}
                onChange={(event) =>
                  setOnRentRequirement((existing) => {
                    return existing && { ...existing, requiredPaymentPercentage: parseFloat(event.target.value) };
                  })
                }
              />
            </div>
          </div>
          {(onRentRequirement.defaultPaymentPercentage || 0) < (onRentRequirement.requiredPaymentPercentage || 0) && (
            <span className="text-[var(--error-500)]">Default percentage must be greater or equal to required</span>
          )}
          {(onRentRequirement.requiredPaymentPercentage || 0) <
            (approvedRequirement.requiredPaymentPercentage || 0) && (
            <span className="text-[var(--error-500)]">
              Required percentage must be greater than or equal to what is required on approval
            </span>
          )}
        </div>
      )}
    </div>
  );
};
